import React, { useEffect, useState } from 'react';
import Button from '../../components/Button/Button';
import { AiOutlineLoading } from "react-icons/ai";
import useFormValidation from '../../hooks/useFormValidation';
import { interviewQuestionFormValidationSchema } from '../../utils/formValidation';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { errorToast } from '../../utils/errorHandling';
import { getInterviewForm, getInterviewQuestion, updateInterviewQuestion } from '../../services/interview';
import { getActiveCourseAjahn } from '../../services/settings';

const base_url = process.env.REACT_APP_BASE_URL

const SubmitInterviewQuestion = () => {
    const { t } = useTranslation();
    const [interviewAjahnsList, setInterviewAjahnsList] = useState([]);
    const [originalData, setOriginalData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { errors, validateField, validateAllFields } = useFormValidation(interviewQuestionFormValidationSchema(t));
    const [formValidationSchema, setFormValidationSchema] = useState([]);
    const [finalAjahn, setFinalAjahn] = useState(null);
    const [wordCountErrors, setWordCountErrors] = useState("");
    const [courseData, setCourseData] = useState(
        localStorage.getItem("courseData")
            ? JSON.parse(localStorage.getItem("courseData"))
            : null
    );
    const languageSelected = localStorage.getItem("i18nextLng");

    const fetchInterviewQuestion = async () => {
        try {
            const data = await getInterviewQuestion();
            if (data) {
                setFormData(data);
                setOriginalData(data);
                setFinalAjahn(data.interview_ajahn);
            }
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const fetchAjahn = async () => {
        try {
            setIsLoading(true);
            const response = await getActiveCourseAjahn();
            if (response.length > 0) {
                setInterviewAjahnsList(response.filter(e => e.is_interview == 1));
            }
            return true;
        } catch (error) {
            throw (error);
        }
    };

    useEffect(() => {
        const appInitiate = async () => {
            try {
                await fetchAjahn();
                await fetchInterviewQuestion();
                setFormValidationSchema(["study_time", "meditation_method", "daily_practice_way", "question", "is_interviewed_before"])
            } catch (error) {
                errorToast(error, t('general.ActionFailed'), t)
            } finally {
                setIsLoading(false);
            }
        }

        appInitiate();
    }, []);

    const wordCountValidate = (inputText, maxWords, fieldName) => {
        const chineseCharCount = (inputText.match(/[\u4e00-\u9fa5]/g) || []).length;
        const englishWordCount = (inputText.match(/\b\w+\b/g) || []).length;
        const totalWordCount = chineseCharCount + englishWordCount;

        if (totalWordCount > maxWords) {
            const error = `${t("interview.WordLimit")}${maxWords} ${t("interview.currentCount")}${totalWordCount}`;
            setWordCountErrors((prevErrors) => ({ ...prevErrors, [fieldName]: error }));
        } else {
            setWordCountErrors((prevErrors) => {
                const { [fieldName]: _, ...updatedErrors } = prevErrors;
                return {
                    ...updatedErrors,
                };
            });
        }
    };

    const handleInputChange = (e) => {
        const { id, value, name } = e.target;
        if (name === "is_interviewed_before") {
            setFormData((prevState) => ({
                ...prevState,
                [name]: id,
            }));
            validateField(name, id);
        } else {
            switch (id) {
                case "study_time":
                    wordCountValidate(value, 10, "study_time");
                    break;
                case "daily_practice_way":
                    wordCountValidate(value, 30, "daily_practice_way");
                    break;
                case "meditation_method":
                    wordCountValidate(value, 30, "meditation_method");
                    break;
                case "question":
                    wordCountValidate(value, 100, "question");
                    break;
            }
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
            validateField(id, value);
        };
    };

    const handleCancel = () => {
        setFormData(originalData);
        setIsEditing(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (validateAllFields(formData, formValidationSchema) && Object.keys(wordCountErrors).length === 0) {
            try {
                const response = await updateInterviewQuestion(formData);
                if (response) {
                    setIsEditing(false);
                    toast.success(t("interview.INT_Suc_Upd"));
                    fetchInterviewQuestion();
                }
            } catch (error) {
                errorToast(error, t("general.failSubmit"), t);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            toast.error(t("general.ValidationFail"));
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {!finalAjahn ?
                <div className='info-container'>
                    <h3>{t("interview.InterviewQuestion")}</h3>
                    <p>
                        {languageSelected === "en"
                            ? courseData.course_ename
                            : courseData.course_cname}
                    </p>
                    {isLoading ?
                        <>
                            <AiOutlineLoading className='spin' />
                            <p>{t("interview.LoadQues")}</p>
                        </>
                        :
                        <>
                            <p style={{ "color": "#ff0000" }}>
                                {t("interview.WaitAssign")}
                            </p>
                            <div className='action-buttons'>
                                <Link to={`${base_url}/view-interview`}>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                    >{t("interview.BTNinterview")}</Button>
                                </Link>
                            </div>
                        </>
                    }
                </div>
                :
                <>
                    <div className='info-container'>
                        <h3>{t("interview.InterviewQuestion")}</h3>
                        <p>
                            {languageSelected === "en"
                                ? courseData.course_ename
                                : courseData.course_cname}
                        </p>
                        {!isEditing ?
                            <div className='action-buttons'>
                                <Button
                                    buttonStyle='btn--primary'
                                    buttonSize='btn--small'
                                    onClick={() => setIsEditing(true)}
                                >{t("interview.BTNeditINT")}</Button>
                            </div>
                            :
                            <div className='action-buttons'>
                                <Button buttonStyle='btn--primary' onClick={handleCancel} type="button">{t("general.BTNcancel")}</Button>
                            </div>
                        }
                    </div>
                    <div className='form-wrapper'>
                        <div className='mt-3 form-section-1'>
                            <p>☘️<strong> {t("interview.InterviewDES")}</strong></p>
                            <p><Trans key="accomodation" html="br:<br/>">
                                {t("interview.DEScription")}
                            </Trans></p>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="form-item">
                                <label>{t("interview.StudyAjahn")}
                                    &nbsp;
                                    {
                                        (formData.interview_ajahn && interviewAjahnsList.length > 0) ?
                                            formData.interview_ajahn === "None" ?
                                                t("Constant.None")
                                                :
                                                languageSelected === "en" ?
                                                    interviewAjahnsList.find((item) => item.id == formData.interview_ajahn)?.ajahn_ename
                                                    :
                                                    interviewAjahnsList.find((item) => item.id == formData.interview_ajahn)?.ajahn_cname
                                            :
                                            t("interview.DecideYET")
                                    }
                                    &nbsp;
                                    {t("interview.BE4")}?
                                    <span style={{ "color": "#ff0000" }}>*</span>
                                </label>
                                <div className='radio'>
                                    <input className="form-check-input" type="radio" disabled={!isEditing} name="is_interviewed_before" id="1" checked={formData.is_interviewed_before === "1"} onChange={handleInputChange} required />
                                    <p className='radioLabel mb-0'>{t("general.yes")}</p>
                                </div>
                                <div className='radio'>
                                    <input className="form-check-input" type="radio" disabled={!isEditing} name="is_interviewed_before" id="0" checked={formData.is_interviewed_before === "0"} onChange={handleInputChange} required />
                                    <p className='radioLabel mb-0'>{t("general.no")}</p>
                                </div>
                            </div>
                            {errors.is_interviewed_before && <span className="error">{errors.is_interviewed_before}</span>}
                            <div className="form-item">
                                <label for="study_time" className="form-label">
                                    {t("interview.HOWlong")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                <textarea type="text" disabled={!isEditing} className="form-control" id="study_time" value={formData.study_time || ""} onChange={handleInputChange} />
                            </div>
                            {errors.study_time && <span className="error">{errors.study_time}</span>}
                            {wordCountErrors.study_time && <span className="error">{wordCountErrors.study_time}</span>}
                            <div className="form-item">
                                <label for="meditation_method" className="form-label">
                                    {t("interview.Meditation_met")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                <textarea type="text" disabled={!isEditing} className="form-control" id="meditation_method" value={formData.meditation_method || ""} onChange={handleInputChange} />
                            </div>
                            {errors.meditation_method && <span className="error">{errors.meditation_method}</span>}
                            {wordCountErrors.meditation_method && <span className="error">{wordCountErrors.meditation_method}</span>}
                            <div className="form-info">
                                <p className='m-0'>{t("interview.Example1")}</p>
                            </div>
                            <div className="form-item">
                                <label for="daily_practice_way" className="form-label">
                                    {t("interview.Practice")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                <textarea type="text" disabled={!isEditing} className="form-control" id="daily_practice_way" value={formData.daily_practice_way || ""} onChange={handleInputChange} />
                            </div>
                            {errors.daily_practice_way && <span className="error">{errors.daily_practice_way}</span>}
                            {wordCountErrors.daily_practice_way && <span className="error">{wordCountErrors.daily_practice_way}</span>}
                            <div className="form-info">
                                <p className='m-0'>{t("interview.Example2")}</p>
                            </div>
                            <div className="form-item">
                                <label for="question" className="form-label">
                                    {t("interview.declare")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                <textarea type="text" disabled={!isEditing} className="form-control" id="question" value={formData.question || ""} onChange={handleInputChange} />
                            </div>
                            {errors.question && <span className="error">{errors.question}</span>}
                            {wordCountErrors.question && <span className="error">{wordCountErrors.question}</span>}
                            {isEditing &&
                                <div className='submit-button d-flex justify-content-center mt-4 gap-3'>
                                    <Button buttonStyle='btn--primary' disabled={isSubmitting} type="submit">
                                        {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsave")}
                                    </Button>
                                </div>
                            }
                        </form>
                    </div>
                </>
            }

        </>
    )
}
export default SubmitInterviewQuestion